/**
 * Auto-scroll state machine for streaming logs.
 *
 * Extracted from steps.ts to isolate the scroll logic and give the
 * CI-findings event bridge a testable boundary.
 */

let elementToScroll: HTMLElement | null = null
let shouldAutoScroll = true
let lastScrollTop: number | null = null
let scrolledUp = false

// When true, auto-scroll is permanently disabled for this page load
// (e.g. when CI Analysis is rendered above the logs).
let autoScrollDisabled = false

/** Permanently disable auto-scroll for the lifetime of this page. */
export function disableAutoScroll() {
  autoScrollDisabled = true
  shouldAutoScroll = false
  scrolledUp = true
  // Cancel any in-flight scroll target so a pending scrollIntoView
  // (scheduled via setTimeout in scrollToLine) becomes a no-op.
  elementToScroll = null
}

/** Whether auto-scroll is currently active. */
export function isAutoScrollEnabled(): boolean {
  return shouldAutoScroll
}

/**
 * Called each time new streaming lines arrive.
 * Updates auto-scroll state based on scroll position/direction.
 * Returns the current shouldAutoScroll value for the caller to decide
 * whether to scroll to the latest line.
 */
export function updateAutoScrollState(lastElementInView: boolean): boolean {
  if (!autoScrollDisabled) {
    // Fallback DOM check: if the React partial mounted before this module
    // loaded, the custom event was missed. Detect its presence now.
    if (document.querySelector('react-partial[partial-name="actions-ci-findings"]')) {
      disableAutoScroll()
    }
  }

  if (autoScrollDisabled) return false

  if (shouldAutoScroll) {
    shouldAutoScroll = !scrolledUp
  } else {
    shouldAutoScroll = lastElementInView
    if (shouldAutoScroll) {
      scrolledUp = false
    }
  }
  return shouldAutoScroll
}

export function determineScrollDirection(newScrollTop: number) {
  if (lastScrollTop !== null) {
    if (!scrolledUp && newScrollTop < lastScrollTop) {
      scrolledUp = true
    }
  }
  lastScrollTop = newScrollTop
}

export function getElementToScroll(): HTMLElement | null {
  return elementToScroll
}

export function setElementToScroll(el: HTMLElement | null) {
  elementToScroll = el
}

export function resetScrollState() {
  lastScrollTop = null
}

/** Reset all internal state — only for use in tests. */
export function resetForTesting() {
  elementToScroll = null
  shouldAutoScroll = true
  lastScrollTop = null
  scrolledUp = false
  autoScrollDisabled = false
}

// Disable auto-scroll when CI Analysis is present on the page.
// Also handles the case where this module loads after the partial has already mounted.
document.addEventListener('actions-ci-findings:mounted', () => disableAutoScroll())
